import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { setTokens, clearTokens, getAccessToken } from '@/api/client'

/**
 * Store autoryzacji — zarządza logowaniem, rejestracją, sesją JWT.
 * Token access (15 min) + refresh (7 dni) w localStorage.
 */

/* Typ użytkownika z backendu */
export interface User {
  id: string
  username: string
  email: string
  role: 'system' | 'disabled' | 'inactive' | 'player' | 'premium' | 'moderator' | 'admin'
  is_staff: boolean
  is_admin: boolean
  created_at: string
}

const AUTH_USER_CACHE_KEY = 'nhex.auth.currentUser.v1'

function cacheCurrentUser(user: User | null): void {
  try {
    if (user) sessionStorage.setItem(AUTH_USER_CACHE_KEY, JSON.stringify(user))
    else sessionStorage.removeItem(AUTH_USER_CACHE_KEY)
  } catch {
    /* sessionStorage can be disabled by browser privacy settings. */
  }
}

function readCachedUser(): User | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(AUTH_USER_CACHE_KEY) || 'null') as Partial<User> | null
    if (!value || typeof value.id !== 'string' || typeof value.username !== 'string' || typeof value.role !== 'string') return null
    return value as User
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  /* ==================== STAN ==================== */
  const user = ref<User | null>(null)
  const initialized = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  let initializePromise: Promise<void> | null = null

  /* ==================== GETTERY ==================== */
  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.is_admin === true)
  const isStaff = computed(() => user.value?.is_staff === true || isAdmin.value)
  const isModerator = computed(() =>
    user.value?.role === 'moderator' || isAdmin.value
  )
  const isPremium = computed(() => user.value?.role === 'premium')
  const username = computed(() => user.value?.username ?? '')
  const userId = computed(() => user.value?.id ?? '')

  /* ==================== AKCJE ==================== */

  /**
   * Inicjalizacja — przywraca sesję z tokenu w localStorage.
   * Wywoływana raz przy starcie (router guard).
   */
  async function initialize(): Promise<void> {
    if (initialized.value) return
    if (initializePromise) return initializePromise

    const token = getAccessToken()
    if (!token) {
      cacheCurrentUser(null)
      initialized.value = true
      return
    }

    initializePromise = (async () => {
      try {
        for (let attempt = 0; attempt < 4; attempt += 1) {
          try {
            const { data } = await api.get<User>('/auth/me')
            user.value = data
            cacheCurrentUser(data)
            error.value = null
            return
          } catch (requestError) {
            const status = (requestError as { response?: { status?: number } })?.response?.status
            const tokenWasRejected = status === 401 || status === 403 || status === 404 || !getAccessToken()
            if (tokenWasRejected) {
              clearTokens()
              cacheCurrentUser(null)
              return
            }
            if ((status === undefined || status >= 500) && attempt < 3) {
              await new Promise(resolve => window.setTimeout(resolve, 200 * (2 ** attempt)))
              continue
            }
            user.value = readCachedUser()
            error.value = 'The service is temporarily unavailable. Your session was preserved.'
            return
          }
        }
      } finally {
        initialized.value = true
        initializePromise = null
      }
    })()
    return initializePromise
  }

  /**
   * Logowanie — zwraca tokeny i dane użytkownika.
   */
  async function login(username: string, password: string, recaptchaToken: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/login', {
        username,
        password,
        recaptcha_token: recaptchaToken,
      })
      setTokens(data.access_token)
      user.value = data.user
      cacheCurrentUser(data.user)
    } catch (err: any) {
      error.value =
        err.response?.data?.error || 'Login failed. Please try again.'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function resendActivation(username: string, password: string, recaptchaToken: string): Promise<string> {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/resend-activation', {
        username,
        password,
        recaptcha_token: recaptchaToken,
      })
      return data.message
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Could not resend the activation email.'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Rejestracja — tworzy konto (wymaga aktywacji emailem).
   */
  async function register(
    username: string,
    email: string,
    password: string,
    recaptchaToken: string,
    onlineLeagueFirstName = '',
    onlineLeagueLastName = '',
    termsAccepted = false,
  ): Promise<void> {
    loading.value = true
    error.value = null
    try {
      await api.post('/auth/register', {
        username,
        email,
        password,
        online_league_first_name: onlineLeagueFirstName,
        online_league_last_name: onlineLeagueLastName,
        terms_accepted: termsAccepted,
        recaptcha_token: recaptchaToken,
      })
    } catch (err: any) {
      error.value =
        err.response?.data?.error || 'Registration failed. Please try again.'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Wylogowanie — unieważnia refresh token na serwerze.
   */
  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch {
      /* Ignoruj błędy — i tak czyścimy tokeny */
    } finally {
      invalidateSession()
    }
  }

  function invalidateSession(): void {
    user.value = null
    error.value = null
    clearTokens()
    cacheCurrentUser(null)
  }

  /**
   * Zmiana hasła (zalogowany użytkownik).
   */
  async function changePassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<void> {
    loading.value = true
    error.value = null
    try {
      await api.post('/auth/change-password', {
        current_password: oldPassword,
        new_password: newPassword,
      })
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Password change failed.'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Reset hasła — wysyła email z linkiem.
   */
  async function forgotPassword(email: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      await api.post('/auth/forgot-password', { email })
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Sending request failed.'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Aktywacja konta z tokenu emailowego.
   */
  async function activateAccount(token: string): Promise<boolean> {
    try {
      await api.get(`/auth/activate/${token}`)
      return true
    } catch {
      return false
    }
  }

  return {
    /* Stan */
    user,
    initialized,
    loading,
    error,
    /* Gettery */
    isAuthenticated,
    isAdmin,
    isStaff,
    isModerator,
    isPremium,
    username,
    userId,
    /* Akcje */
    initialize,
    login,
    resendActivation,
    register,
    logout,
    changePassword,
    forgotPassword,
    activateAccount,
    invalidateSession,
  }
})
